import { Effect, Schema, SchemaGetter, SchemaIssue } from "effect";

// These shallow envelopes preserve the legacy order of field validation. They
// make no claim about the contents, which their owning Schemas decode next.
export const IdentityObjectInput = /* @__PURE__ */ (() => Schema.declare((value): value is object =>
  typeof value === "object" && value !== null
))();
export const IdentityArrayInput = /* @__PURE__ */ (() => Schema.declare((value): value is readonly unknown[] =>
  Array.isArray(value)
))();
export const UniqueIdentityValues = /* @__PURE__ */ (() => IdentityArrayInput.check(Schema.makeFilter(values =>
  new Set(values).size === values.length
)))();
export const IdentityRecordInput = /* @__PURE__ */ (() => IdentityObjectInput.check(Schema.makeFilter(value =>
  !Array.isArray(value)
)))();

/** Inspect descriptors before decoding authority; never invoke input getters. */
export const plainIdentityData = (fields: readonly string[], label: string) => Schema.ObjectKeyword.annotate({ identifier: "a plain object" }).pipe(
  Schema.decodeTo(Schema.Record(Schema.String, Schema.Unknown), {
    decode: SchemaGetter.transformOrFail(input => Effect.try({
      try: () => {
        const prototype = Object.getPrototypeOf(input);
        if (prototype !== Object.prototype && prototype !== null) throw new SchemaIssue.InvalidValue({ message: `${label} must be a plain object.` });
        const descriptors = Object.getOwnPropertyDescriptors(input);
        const snapshot: Record<string, unknown> = Object.create(null);
        for (const key of Reflect.ownKeys(descriptors)) {
          if (typeof key !== "string" || !fields.includes(key)) throw new SchemaIssue.InvalidValue({ message: `${label} contains an unknown field.` });
          const descriptor = descriptors[key];
          if (!descriptor || !descriptor.enumerable || !("value" in descriptor)) throw new SchemaIssue.InvalidValue({ message: `${label} must use data properties.` });
          if (descriptor.value !== undefined) snapshot[key] = descriptor.value;
        }
        return snapshot;
      },
      catch: cause => SchemaIssue.isIssue(cause) ? cause : new SchemaIssue.InvalidValue({ message: `${label} must be a readable plain object.` })
    })), encode: SchemaGetter.transform(value => value)
  })
);
export const exactIdentityFields = (required: readonly string[], optional: readonly string[]) =>
  IdentityObjectInput.check(Schema.makeFilter(value =>
    required.every(key => Object.hasOwn(value, key)) &&
    Object.keys(value).every(key => required.includes(key) || optional.includes(key))
  ));

export const ContentIdentityText = /* @__PURE__ */ (() => Schema.String.check(Schema.makeFilter(value =>
  value.length >= 1 && value.length <= 256 && value.trim() === value && !/[\x00-\x1f\x7f]/.test(value)
)))();
export const Sha256Identity = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^sha256:[0-9a-f]{64}$/)))();

export const CompatibilityDate = /* @__PURE__ */ (() => ContentIdentityText.check(Schema.makeFilter(value => {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(parsed.valueOf()) &&
    parsed.toISOString().slice(0, 10) === value;
})))();
